/**
 * LocationForm — vista de detalle a pantalla completa (estilo Odoo).
 *
 * Pestañas: General / Notas.  SmartButtons: tipo, código, dirección, edificio,
 * ubicación padre.
 *
 * ESTÁNDAR DEL CMDB (ver `docs/ESTANDAR_LISTADOS.md`):
 *  · una ubicación existente abre EN LECTURA y solo se edita pulsando «Editar»;
 *  · el alta nueva (y el botón lápiz del listado) abre directamente editable;
 *  · la copia pregunta el nombre en el diálogo común (`common/CloneDialog`);
 *  · el borrado se confirma con el diálogo común (`common/Dialog`).
 *
 * Los padres seleccionables salen de `utils/locationTree.eligibleParents`, la
 * misma función que usa el listado: así no se puede colgar una ubicación de sí
 * misma ni de una de sus hijas.
 */

import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  MapPinIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  PowerIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import DocSmartButton from './DocSmartButton';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import CiActivityPanel from './common/CiActivityPanel';
import LocationDeleteInfo from './LocationDeleteInfo';
import { eligibleParents as eligibleParentsOf } from '../utils/locationTree';
import { registroGuardado } from '../utils/savedRecord';
import '../styles/DetailView.css';
import DetailCrumb from './DetailCrumb';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import LocationOrgChart from './LocationOrgChart';

const TYPE_INFO = {
  building:  { icon: '🏢', label: 'Edificio', color: '#0369a1' },
  office:    { icon: '🏬', label: 'Oficina',  color: '#0891b2' },
  warehouse: { icon: '🏭', label: 'Nave',     color: '#b45309' },
  floor:     { icon: '📶', label: 'Planta',   color: '#7c3aed' },
  room:      { icon: '🚪', label: 'Sala',     color: '#059669' },
  row:       { icon: '📦', label: 'Fila',     color: '#d97706' },
};

// Tipos con dirección postal (el resto son divisiones internas).
const TIPOS_CON_DIRECCION = ['building', 'office', 'warehouse'];
// Tipo sugerido al crear una hija desde el organigrama.
const TIPO_HIJO = { building: 'floor', office: 'room', warehouse: 'room', floor: 'room', room: 'row' };

function LocationForm({ location, companyId, initialMode = 'create', onClose, onSave, variant = 'classic', siblingLocations = [], onNavigate }) {
  const isCards = variant === 'cards';
  const isExisting = Boolean(location?.id);
  const navigate = useNavigate();

  // Modo lectura por defecto en las fichas existentes; el alta y el botón
  // «Editar» del listado piden `initialMode="edit"`.
  const [editing,       setEditing]       = useState(!isExisting || initialMode === 'edit');
  const [showClone,     setShowClone]     = useState(false);
  const [cloneName,     setCloneName]     = useState('');
  const [cloning,       setCloning]       = useState(false);
  const [cloneError,    setCloneError]    = useState('');
  const [showDelete,    setShowDelete]    = useState(false);
  // Alcance del borrado: lo pide el diálogo al abrirse para poder decir qué
  // elementos se quedarán SIN ubicación (no se borran).
  const [deleteInfo,    setDeleteInfo]    = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location_type: 'building',
    parent_id: null,
    description: '',
    address: '',
    floor: '',
    building_name: '',
    notes: '',
    is_active: true,
  });
  const [isDirty,      setIsDirty]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [savedOk,      setSavedOk]      = useState(false);
  const [error,        setError]        = useState('');
  const [activeTab,    setActiveTab]    = useState('general');
  const [allLocations, setAllLocations] = useState([]);
  // KPIs de la sede: cuántos CIs y redes cuelgan de ella (incluida su rama).
  const [kpis,         setKpis]         = useState(null);
  // Estado «limpio» de la ficha: es lo que restaura «Descartar» tras editar.
  const [baseline,     setBaseline]     = useState(location || null);
  // Último nombre guardado: el título en línea se compara con esto y no con la
  // fila del listado, que puede venir desfasada tras renombrar.
  const [committedName, setCommittedName] = useState(location?.name || '');

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty || (formData.name || '') !== committedName,
    saving,
    entity: 'esta ubicación',
    actionLabel: 'pasas a otra ubicación o cierras la ficha',
    save: () => handleSubmit(null, { force: true, keepEditing: editing }),
  });

  useEffect(() => {
    if (location) {
      setFormData(prev => ({ ...prev, ...location }));
      setBaseline(location);
      setCommittedName(location.name || '');
    }
  }, [location]);

  const recargarUbicaciones = useCallback(() => {
    if (!companyId) return Promise.resolve();
    return api.getLocations('', companyId)
      .then(res => setAllLocations(res.data || []))
      .catch(() => {});
  }, [companyId]);

  useEffect(() => { recargarUbicaciones(); }, [recargarUbicaciones]);

  // El resumen se pide aparte: el backend lo calcula acumulando la rama.
  useEffect(() => {
    if (!companyId || !location?.id) { setKpis(null); return undefined; }
    let cancelado = false;
    api.getLocationsSummary(companyId)
      .then(res => { if (!cancelado) setKpis((res?.data || {})[String(location.id)] || null); })
      .catch(() => { if (!cancelado) setKpis(null); });
    return () => { cancelado = true; };
  }, [companyId, location?.id]);

  // ── Subredes IPAM de esta ubicación ───────────────────────────────────────
  // Se muestran las que apuntan a la propia ubicación (el KPI «Redes» suma
  // además las de toda su rama).
  const [subnets,        setSubnets]        = useState([]);
  const [loadingSubnets, setLoadingSubnets] = useState(false);
  const [subnetsError,   setSubnetsError]   = useState('');
  const [allSubnets,     setAllSubnets]     = useState([]);   // para vincular
  const [linkOpen,       setLinkOpen]       = useState(false);
  const [linkTarget,     setLinkTarget]     = useState('');
  const [linkBusy,       setLinkBusy]       = useState(false);
  const [linkInfo,       setLinkInfo]       = useState(null);

  const recargarSubredes = useCallback(() => {
    if (!companyId || !location?.id) return Promise.resolve();
    setLoadingSubnets(true);
    setSubnetsError('');
    return api.getLocationSubnets(location.id, companyId)
      .then(res => setSubnets(res.data || []))
      .catch(err => setSubnetsError(err.message || 'No se pudieron cargar las subredes'))
      .finally(() => setLoadingSubnets(false));
  }, [companyId, location?.id]);

  useEffect(() => { recargarSubredes(); }, [recargarSubredes]);

  const abrirVincular = () => {
    setLinkInfo(null);
    setLinkTarget('');
    setSubnetsError('');
    setLinkOpen(true);
    api.getIpamSubnets(companyId)
      .then(res => setAllSubnets(res.data || []))
      .catch(err => setSubnetsError(err.message || 'No se pudieron cargar las subredes'));
  };

  const vincularSubred = async () => {
    if (!linkTarget) return;
    setLinkBusy(true);
    setSubnetsError('');
    try {
      const res = await api.updateIpamSubnet(linkTarget, { location_id: location.id }, companyId);
      const movidos = res?.location_applied?.moved || 0;
      setLinkInfo(
        movidos
          ? `Subred vinculada. ${movidos} equipo(s) se han movido a ${formData.name}.`
          : 'Subred vinculada. No había equipos con IP en esa red.',
      );
      setLinkTarget('');
      await recargarSubredes();
      // El KPI «Redes» cambia al vincular.
      api.getLocationsSummary(companyId)
        .then(r => setKpis((r?.data || {})[String(location.id)] || null))
        .catch(() => {});
    } catch (err) {
      setSubnetsError(err.message || 'Error vinculando la subred');
    } finally {
      setLinkBusy(false);
    }
  };

  const desvincularSubred = async (subnet) => {
    setSubnetsError('');
    try {
      await api.updateIpamSubnet(subnet.id, { location_id: null }, companyId);
      await recargarSubredes();
      api.getLocationsSummary(companyId)
        .then(r => setKpis((r?.data || {})[String(location.id)] || null))
        .catch(() => {});
    } catch (err) {
      setSubnetsError(err.message || 'Error desvinculando la subred');
    }
  };

  const setField = (name, value) => {
    if (!editing) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setError('');
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleDiscard = () => {
    // En una ficha existente, descartar vuelve a modo LECTURA (así siempre hay
    // salida de la edición, aunque no se haya tocado nada). En un alta, cierra.
    if (baseline) {
      setFormData(prev => ({ ...prev, ...baseline }));
      setEditing(false);
    } else {
      onClose();
    }
    setIsDirty(false);
    setError('');
  };

  const handleSubmit = async (e, { force = false, keepEditing = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    // `force`: guardar solo el nombre desde el título en reposo (Enter).
    if (!editing && !force) return false;
    if (!formData.name.trim()) { setError('El nombre es obligatorio'); return false; }
    setSaving(true);
    setError('');
    try {
      let result;
      if (isExisting) result = await api.updateLocation(location.id, formData, companyId);
      else            result = await api.createLocation(formData, companyId);
      if (result.success || result.data) {
        setIsDirty(false);
        setCommittedName(formData.name || '');
        // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
        // con id, así se encienden las zonas que necesitan estar guardado.
        onSave?.(isExisting ? null : registroGuardado(result));
        if (isExisting) {
          // Se guarda y la ficha vuelve a LECTURA (el estándar no deja la
          // ficha editable después de guardar).
          setBaseline(formData);
          if (!keepEditing) setEditing(false);
          setSavedOk(true);
          setTimeout(() => setSavedOk(false), 3000);
        }
        return true;
      } else {
        throw new Error(result.message || 'Error al guardar');
      }
    } catch (err) { setError(err.message); return false; }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError('');
    try {
      // Borrado FÍSICO. Los elementos no se borran: se quedan sin ubicación.
      await api.deleteLocation?.(location.id, companyId);
      setShowDelete(false);
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err.message || 'Error eliminando');
      setShowDelete(false);
    }
    finally { setSaving(false); }
  };

  const openDeleteDialog = () => {
    setShowDelete(true);
    setDeleteInfo(null);
    if (!companyId || !location?.id) return;
    setLoadingDelete(true);
    api.getLocationDeletePreview(location.id, companyId)
      .then((res) => setDeleteInfo(res?.data || null))
      .catch(() => setDeleteInfo(null))
      .finally(() => setLoadingDelete(false));
  };

  /** Desactivar: se conserva la ficha, deja de salir en listados y selectores. */
  const handleDeactivate = async () => {
    setSaving(true);
    setError('');
    try {
      await api.deleteLocation?.(location.id, companyId, { mode: 'deactivate' });
      setShowDelete(false);
      setFormData(prev => ({ ...prev, is_active: false }));
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err.message || 'Error desactivando');
      setShowDelete(false);
    }
    finally { setSaving(false); }
  };

  /** Volver a activar una ubicación desactivada (no toca nada más). */
  const handleActivate = async () => {
    setSaving(true);
    setError('');
    try {
      await api.updateLocation(location.id, { is_active: true }, companyId);
      setFormData(prev => ({ ...prev, is_active: true }));
      setBaseline(prev => (prev ? { ...prev, is_active: true } : prev));
      onSave?.();
    } catch (err) {
      setError(err.message || 'Error activando');
    }
    finally { setSaving(false); }
  };

  const openCloneModal = () => {
    setCloneName(`${formData.name || 'Ubicación'} (copia)`);
    setCloneError('');
    setShowClone(true);
  };

  const handleCloneConfirm = async () => {
    const name = cloneName.trim();
    if (!name || cloning) return;
    setCloning(true);
    setCloneError('');
    try {
      await api.cloneLocation(location.id, { name }, companyId);
      setShowClone(false);
      onSave?.();
      onClose?.();
    } catch (err) {
      setCloneError(err.message || 'No se pudo copiar la ubicación');
    } finally {
      setCloning(false);
    }
  };

  const typeInfo    = TYPE_INFO[formData.location_type] || TYPE_INFO.building;
  const parentLocation = allLocations.find(l => l.id === formData.parent_id) || null;
  // Padres válidos: activos, ni ella misma ni sus descendientes (misma función
  // que usa el listado, para que ambos ofrezcan exactamente el mismo árbol).
  const eligibleParents = eligibleParentsOf(allLocations, isExisting ? location.id : null);
  const statusStyle = formData.is_active
    ? { bg: 'rgba(22, 163, 74, 0.14)', color: 'var(--green-600)', label: 'Activa' }
    : { bg: 'rgba(220, 38, 38, 0.14)', color: 'var(--red-600)', label: 'Inactiva' };

  // ── Pasar de ficha sin volver al listado ──
  const nameDirty = (formData.name || '') !== committedName;
  const locIndex = isExisting ? siblingLocations.findIndex(l => l.id === location.id) : -1;

  const irAUbicacion = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setError('');
      onNavigate?.(destino);
    });
  };

  // ¿Este tipo de ubicación lleva dirección postal?
  const conDireccion = TIPOS_CON_DIRECCION.includes(formData.location_type);
  const direccionCompleta = [
    formData.address, formData.postal_code, formData.city, formData.province, formData.country,
  ].filter(Boolean).join(', ');
  const mapsUrl = direccionCompleta
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccionCompleta)}`
    : '';

  // ── Alta rápida desde el organigrama ──
  const [quick, setQuick] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickError, setQuickError] = useState('');

  const abrirAltaRapida = (padre) => {
    setQuickError('');
    setQuick({
      parentId: padre.id,
      parentName: padre.name,
      name: '',
      type: TIPO_HIJO[padre.location_type] || 'room',
    });
  };

  const crearRapida = async (otra = false) => {
    if (!quick) return;
    if (!quick.name.trim()) { setQuickError('Escribe un nombre.'); return; }
    setQuickBusy(true);
    setQuickError('');
    try {
      await api.createLocation({
        name: quick.name.trim(),
        location_type: quick.type,
        parent_id: quick.parentId,
      }, companyId);
      await recargarUbicaciones();
      // «Crear y nuevo»: se queda el modal abierto con el mismo tipo y padre.
      if (otra) setQuick(q => (q ? { ...q, name: '' } : q));
      else setQuick(null);
    } catch (err) {
      setQuickError(err.message || 'No se pudo crear la ubicación');
    } finally {
      setQuickBusy(false);
    }
  };

  // ── Renombrado rápido desde el organigrama ──
  const [rename, setRename] = useState(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState('');

  const abrirRenombrar = (loc) => {
    setRenameError('');
    setRename({ id: loc.id, name: loc.name });
  };

  const guardarRenombre = async () => {
    if (!rename) return;
    if (!rename.name.trim()) { setRenameError('Escribe un nombre.'); return; }
    setRenameBusy(true);
    setRenameError('');
    try {
      await api.updateLocation(rename.id, { name: rename.name.trim() }, companyId);
      await recargarUbicaciones();
      // Si se renombra la ubicación que tenemos abierta, reflejarlo en la ficha.
      if (isExisting && String(location.id) === String(rename.id)) {
        setFormData(prev => ({ ...prev, name: rename.name.trim() }));
        setBaseline(prev => (prev ? { ...prev, name: rename.name.trim() } : prev));
      }
      setRename(null);
    } catch (err) {
      setRenameError(err.message || 'No se pudo renombrar la ubicación');
    } finally {
      setRenameBusy(false);
    }
  };

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Ubicaciones
        </button>
        <DetailCrumb label={formData.name} />

        {/* Contador de fichas: a la izquierda, tras la migaja (§3.1). */}
        <FormPager
          items={siblingLocations}
          index={locIndex}
          label="Ubicación"
          disabled={saving}
          onChange={irAUbicacion}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: 'var(--green-600)', fontSize: '0.78rem', fontWeight: 600 }}>
            <CheckIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> Guardado
          </span>
        )}
        {error && (
          <span style={{ color: 'var(--red-600)', fontSize: '0.78rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <ExclamationTriangleIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> {error}
          </span>
        )}
        <div className="dv-topbar-actions">
          {/* El estado vive en la cabecera (badge) y se cambia con el interruptor
              de la tarjeta General; en el topbar solo quedan acciones. */}
          {!isCards && (
          <button
            type="button"
            className="dv-status-badge"
            style={{
              background: statusStyle.bg,
              color: statusStyle.color,
              border: 0,
              cursor: editing ? 'pointer' : 'default',
            }}
            onClick={() => { if (editing) setField('is_active', !formData.is_active); }}
            disabled={!editing}
            title={editing
              ? 'Pulsar para activar o desactivar la ubicación'
              : 'Ubicación activa o inactiva'}
          >
            {statusStyle.label}
          </button>
          )}

          {!editing && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={openCloneModal}
              title="Copiar esta ubicación con su configuración"
            >
              <DocumentDuplicateIcon />
              Copiar
            </button>
          )}
          {!editing && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={() => setEditing(true)}
              title="Editar la ficha"
            >
              <PencilSquareIcon />
              Editar
            </button>
          )}

          {editing && !isCards && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          {editing && !isCards && (
            <button
              className="dv-btn dv-btn-primary"
              type="button"
              onClick={handleSubmit}
              disabled={saving || !isDirty}
            >
              {saving ? <span className="dv-spinner" /> : <CheckIcon />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}

          {isExisting && !editing && !isCards && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={formData.is_active ? handleDeactivate : handleActivate}
              disabled={saving}
              title={formData.is_active
                ? 'Desactivar la ubicación (se conserva; deja de salir en listados y selectores)'
                : 'Volver a activar la ubicación'}
            >
              <PowerIcon />
              {formData.is_active ? 'Desactivar' : 'Activar'}
            </button>
          )}

          {isExisting && (
            <button
              className="dv-btn dv-btn-danger"
              type="button"
              onClick={openDeleteDialog}
              disabled={saving}
              title="Eliminar la ubicación"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo ficha clásica; en tarjetas los datos van a chips) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button">
          <span className="dv-smart-btn-value" style={{ fontSize: '1.2rem' }}>{typeInfo.icon}</span>
          <span className="dv-smart-btn-label" style={{ color: typeInfo.color }}>{typeInfo.label}</span>
        </button>

        {formData.code && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{formData.code}</span>
            <span className="dv-smart-btn-label">Código</span>
          </button>
        )}

        {formData.address && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-icon">
              <MapPinIcon style={{ width: 18, height: 18, color: 'var(--text-secondary)' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              {formData.address}
            </span>
            <span className="dv-smart-btn-label">Dirección</span>
          </button>
        )}

        {(formData.building_name || formData.floor) && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-value" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              🏢 {[formData.building_name, formData.floor].filter(Boolean).join(' · ')}
            </span>
            <span className="dv-smart-btn-label">Edificio / Planta</span>
          </button>
        )}

        {parentLocation && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem', color: '#0369a1' }}>
              {TYPE_INFO[parentLocation.location_type]?.icon || '📍'} {parentLocation.name}
            </span>
            <span className="dv-smart-btn-label">Ubicación padre</span>
          </button>
        )}

        {isExisting && (
          <DocSmartButton elementType="location" elementId={location.id} elementName={formData.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<span style={{ fontSize: '1.15rem' }}>{typeInfo.icon}</span>}
                title={formData.name}
                onTitleChange={(v) => {
                  // Se escribe directo en el estado: `setField` ignora los cambios
                  // cuando no se está editando, y el título se edita en lectura.
                  setFormData(prev => ({ ...prev, name: v }));
                  setIsDirty(true);
                  setError('');
                }}
                placeholder="Nombre de la ubicación…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                dirty={isDirty || nameDirty}
                titleDirty={nameDirty}
                onTitleCommit={() => handleSubmit(null, { force: true })}
                onTitleReset={handleDiscard}
                crumb={[
                  typeInfo.label,
                  parentLocation ? parentLocation.name : null,
                  formData.building_name || null,
                  formData.floor ? `· ${formData.floor}` : null,
                ].filter(Boolean).join(' › ')}
                badges={[
                  {
                    text: formData.is_active ? 'Activa' : 'Inactiva',
                    tone: formData.is_active ? 'ok' : 'bad',
                  },
                ]}
                chips={[
                  ...(formData.code ? [{ value: formData.code, label: 'código' }] : []),
                  ...(formData.address ? [{ value: formData.address, label: 'dirección' }] : []),
                  ...((formData.building_name || formData.floor)
                    ? [{ value: [formData.building_name, formData.floor].filter(Boolean).join(' · '), label: 'edificio / planta' }]
                    : []),
                  ...(parentLocation
                    ? [{ value: `${TYPE_INFO[parentLocation.location_type]?.icon || '📍'} ${parentLocation.name}`, label: 'ubicación padre' }]
                    : []),
                ]}
                extra={isExisting ? (
                  <DocSmartButton elementType="location" elementId={location.id} elementName={formData.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
          <input
            className="dv-record-title"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Nombre de la ubicación…"
            required
            disabled={!editing}
            autoFocus={!isExisting}
          />
            )}
            tabs={(
          <div className="dv-tabs-nav">
            <button type="button" className={`dv-tab-btn${activeTab === 'general' ? ' active' : ''}`} onClick={() => setActiveTab('general')}>General</button>
            {isExisting && (
              <button type="button" className={`dv-tab-btn${activeTab === 'subnets' ? ' active' : ''}`} onClick={() => setActiveTab('subnets')}>
                Subredes{subnets.length ? ` (${subnets.length})` : ''}
              </button>
            )}
          </div>
            )}
          >

          {activeTab === 'general' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              {isCards && kpis && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>CIs</span><b>{kpis.total || 0}</b></div>
                  <div className="sf2-kpi"><span>Equipos</span><b>{kpis.by_type?.equipos || 0}</b></div>
                  <div className="sf2-kpi"><span>Racks</span><b>{kpis.by_type?.racks || 0}</b></div>
                  <div className="sf2-kpi"><span>Estaciones</span><b>{kpis.by_type?.estaciones || 0}</b></div>
                  <div className="sf2-kpi"><span>Redes</span><b>{kpis.subnets || 0}</b></div>
                </div>
              )}
              <div className={isCards ? 'sf2-grid sf2-grid--2' : ''}>
              <div className={`dv-section${isCards ? ' sf2-card' : ''}`}>
                <div className="dv-section-title">Datos de la ubicación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código</label>
                    <input
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="CPD-MAD-01"
                      disabled={!editing}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo</label>
                    <select
                      name="location_type"
                      value={formData.location_type}
                      onChange={handleChange}
                      className="form-select"
                      disabled={!editing}
                    >
                      <option value="building">🏢 Edificio</option>
                      <option value="office">🏬 Oficina</option>
                      <option value="warehouse">🏭 Nave</option>
                      <option value="floor">📶 Planta</option>
                      <option value="room">🚪 Sala</option>
                      <option value="row">📦 Fila</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ubicación padre</label>
                    <select
                      name="parent_id"
                      value={formData.parent_id ?? ''}
                      onChange={e => setField('parent_id', e.target.value ? parseInt(e.target.value) : null)}
                      className="form-select"
                      disabled={!editing}
                    >
                      <option value="">— Sin padre (raíz) —</option>
                      {eligibleParents.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {TYPE_INFO[loc.location_type]?.icon || '📍'} {loc.name}
                          {loc.code ? ` (${loc.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {conDireccion ? (
                    <>
                      <div className="col-md-8">
                        <label className="form-label">Dirección</label>
                        <input
                          name="address"
                          value={formData.address || ''}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Calle Ejemplo 123"
                          disabled={!editing}
                        />
                        {mapsUrl && (
                          <a className="sf2-link" href={mapsUrl} target="_blank" rel="noreferrer">
                            Buscar en Google Maps →
                          </a>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Código postal</label>
                        <input
                          name="postal_code"
                          value={formData.postal_code || ''}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="28001"
                          disabled={!editing}
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Población</label>
                        <input
                          name="city"
                          value={formData.city || ''}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Madrid"
                          disabled={!editing}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Provincia</label>
                        <input
                          name="province"
                          value={formData.province || ''}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Madrid"
                          disabled={!editing}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">País</label>
                        <input
                          name="country"
                          value={formData.country || ''}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="España"
                          disabled={!editing}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="col-md-6">
                      <label className="form-label">Planta / Piso</label>
                      <input
                        name="floor"
                        value={formData.floor || ''}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="P1 / 1ª / PB"
                        disabled={!editing}
                      />
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <input
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Descripción breve"
                      disabled={!editing}
                    />
                  </div>
                  {/* Estado: el badge de la cabecera informa, aquí se cambia
                      (§12.D7, un solo control). */}
                  <div className="col-12">
                    <label className="form-label">Estado</label>
                    <label
                      className="sf2-switch sf2-switch--sm"
                      title="Una ubicación inactiva se conserva, pero deja de salir en listados y selectores"
                    >
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={!!formData.is_active}
                        onChange={handleChange}
                        disabled={!editing}
                      />
                      <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                      <span className="sf2-switch-label">{formData.is_active ? 'Activa' : 'Inactiva'}</span>
                    </label>
                    <div className="sf2-hint">Inactiva: se conserva, pero deja de salir en listados y selectores.</div>
                  </div>
                </div>
              </div>
              {isCards && (
                <div className="dv-section sf2-card">
                  <div className="dv-section-title">Organigrama de ubicaciones</div>
                  <p className="form-text" style={{ marginBottom: 10 }}>
                    Usa el botón <b>+</b> de cualquier ubicación para crear una hija (planta,
                    sala, despacho…) sin salir de la ficha. La resaltada es la que estás viendo.
                  </p>
                  <LocationOrgChart
                    locations={allLocations}
                    currentId={isExisting ? location.id : null}
                    onAdd={abrirAltaRapida}
                    onRename={abrirRenombrar}
                  />
                </div>
              )}
              {isCards && kpis && (
                <div className="dv-section sf2-card">
                  <div className="dv-section-title">Qué hay en esta sede</div>
                  <p className="form-text" style={{ marginBottom: 10 }}>
                    Recuento de la propia ubicación más todo lo que cuelga de ella
                    (plantas, salas, filas…).
                  </p>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    {[
                      ['equipos',     'Equipos en rack'],
                      ['racks',       'Racks'],
                      ['estaciones',  'Estaciones de trabajo'],
                      ['impresoras',  'Impresoras'],
                      ['perifericos', 'Periféricos'],
                      ['telefonos',   'Teléfonos IP'],
                      ['wifi',        'Puntos Wi-Fi'],
                      ['chasis',      'Chasis'],
                      ['tomas',       'Tomas de pared'],
                    ].map(([clave, etiqueta]) => (
                      <div className="sf2-field" key={clave}>
                        <label>{etiqueta}</label>
                        <div className="v">{kpis.by_type?.[clave] || 0}</div>
                      </div>
                    ))}
                    <div className="sf2-field">
                      <label>Redes (subredes IPAM)</label>
                      <div className="v">{kpis.subnets || 0}</div>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {activeTab === 'subnets' && isExisting && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className={`dv-section${isCards ? ' sf2-card' : ''}`}>
                <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span>Redes vinculadas a esta ubicación</span>
                  <button type="button" className="dv-btn dv-btn-primary" onClick={abrirVincular}>
                    ＋ Vincular subred
                  </button>
                </div>
                <p className="form-text" style={{ marginBottom: 10 }}>
                  Los equipos cuya IP caiga dentro de una de estas subredes se colocan aquí
                  automáticamente, y se mueven solos si el agente detecta otra IP.
                  {kpis?.subnets ? ` La rama completa suma ${kpis.subnets} red(es).` : ''}
                </p>
                {linkInfo && <div className="alert alert-success py-2" style={{ fontSize: '0.88em' }}>{linkInfo}</div>}
                {subnetsError && <div className="alert alert-danger py-2" style={{ fontSize: '0.88em' }}>{subnetsError}</div>}
                {loadingSubnets && <p className="list-hint">Cargando subredes…</p>}
                {!loadingSubnets && subnets.length === 0 && (
                  <p className="list-hint" style={{ margin: 0 }}>
                    Todavía no hay ninguna subred vinculada. Pulsa «Vincular subred» para elegir una del IPAM.
                  </p>
                )}
                {!loadingSubnets && subnets.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>CIDR</th>
                          <th>Nombre</th>
                          <th>Tipo</th>
                          <th>VLAN</th>
                          <th>Ocupación</th>
                          <th aria-label="acciones" />
                        </tr>
                      </thead>
                      <tbody>
                        {subnets.map(subnet => (
                          <tr key={subnet.id}>
                            <td><span className="list-code" style={{ fontWeight: 700 }}>{subnet.cidr}</span></td>
                            <td>{subnet.name || <span className="list-hint">—</span>}</td>
                            <td>{subnet.type_label || subnet.type || '—'}</td>
                            <td>{subnet.vlan_number != null ? `VLAN ${subnet.vlan_number}` : '—'}</td>
                            <td>
                              {subnet.usable_ips
                                ? `${subnet.occupation_pct ?? 0}% · ${subnet.used_ips}/${subnet.usable_ips}`
                                : '—'}
                            </td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button
                                type="button"
                                className="dv-btn dv-btn-secondary"
                                title="Abrir la subred en el IPAM"
                                onClick={() => navigate(`/networks/subnets?destacar=${subnet.id}&abrir=1`)}
                              >
                                Ver en IPAM
                              </button>
                              {' '}
                              <button
                                type="button"
                                className="dv-btn dv-btn-secondary"
                                title="Dejar de vincular la subred a esta ubicación"
                                onClick={() => desvincularSubred(subnet)}
                              >
                                Desvincular
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null, { force: true })}
          />
          </FormCardsShell>
        </form>

        {/* ── Panel derecho: actividad y comentarios (compartido) ── */}
        <CiActivityPanel
          companyId={companyId}
          ciType="location"
          ciId={isExisting ? location.id : null}
          tipoLegible="la ubicación"
        />
      </div>

      {/* ── Alta rápida de una ubicación hija (desde el organigrama) ── */}
      {quick && (
        <Dialog
          title="Nueva ubicación dentro de…"
          icon="➕"
          onClose={() => { if (!quickBusy) setQuick(null); }}
          busy={quickBusy}
          footer={(
            <>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setQuick(null)} disabled={quickBusy}>
                Cancelar
              </button>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={() => crearRapida(true)} disabled={quickBusy || !quick.name.trim()}>
                Crear y nuevo
              </button>
              <button type="button" className="dv-btn dv-btn-primary" onClick={() => crearRapida(false)} disabled={quickBusy}>
                {quickBusy ? 'Creando…' : 'Crear'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="form-text" style={{ margin: 0 }}>
              Se creará dentro de <b>{quick.parentName}</b>.
            </div>
            <div>
              <label className="form-label">Nombre</label>
              <input
                className="form-control"
                value={quick.name}
                onChange={e => setQuick(q => ({ ...q, name: e.target.value }))}
                placeholder="ej. Sala de reuniones"
                autoFocus
              />
            </div>
            <div>
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={quick.type}
                onChange={e => setQuick(q => ({ ...q, type: e.target.value }))}
              >
                <option value="floor">📶 Planta</option>
                <option value="room">🚪 Sala</option>
                <option value="office">🏬 Oficina</option>
                <option value="warehouse">🏭 Nave</option>
                <option value="row">📦 Fila</option>
              </select>
            </div>
            {quickError && <div className="text-danger small">{quickError}</div>}
          </div>
        </Dialog>
      )}

      {/* ── Renombrar una ubicación desde el organigrama ── */}
      {rename && (
        <Dialog
          title="Cambiar nombre"
          icon="✏️"
          onClose={() => { if (!renameBusy) setRename(null); }}
          busy={renameBusy}
          footer={(
            <>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setRename(null)} disabled={renameBusy}>
                Cancelar
              </button>
              <button type="button" className="dv-btn dv-btn-primary" onClick={guardarRenombre} disabled={renameBusy}>
                {renameBusy ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label className="form-label">Nombre</label>
              <input
                className="form-control"
                value={rename.name}
                onChange={e => setRename(r => ({ ...r, name: e.target.value }))}
                autoFocus
              />
            </div>
            {renameError && <div className="text-danger small">{renameError}</div>}
          </div>
        </Dialog>
      )}

      {/* ── Vincular una subred IPAM a esta ubicación ── */}
      {linkOpen && (
        <Dialog
          title="Vincular subred a esta ubicación"
          icon="🔗"
          onClose={() => { if (!linkBusy) setLinkOpen(false); }}
          busy={linkBusy}
          footer={(
            <>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setLinkOpen(false)} disabled={linkBusy}>
                Cerrar
              </button>
              <button type="button" className="dv-btn dv-btn-primary" onClick={vincularSubred} disabled={linkBusy || !linkTarget}>
                {linkBusy ? 'Vinculando…' : 'Vincular'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <p className="form-text" style={{ margin: 0 }}>
              Elige una subred del IPAM. Los equipos cuya IP caiga dentro pasarán a
              <b> {formData.name}</b> y se moverán solos cuando cambie su IP.
            </p>
            <div>
              <label className="form-label">Subred sin ubicación</label>
              <select
                className="form-select"
                value={linkTarget}
                onChange={e => setLinkTarget(e.target.value)}
              >
                <option value="">— Selecciona una subred —</option>
                {allSubnets
                  .filter(subnet => !subnet.location_id)
                  .map(subnet => (
                    <option key={subnet.id} value={subnet.id}>
                      {subnet.cidr}{subnet.name ? ` · ${subnet.name}` : ''}
                    </option>
                  ))}
              </select>
              {allSubnets.filter(subnet => !subnet.location_id).length === 0 && (
                <div className="form-text">
                  No quedan subredes sin ubicación. Quita el vínculo de otra ficha o crea una en el IPAM.
                </div>
              )}
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Copiar ubicación (diálogo común) ── */}
      {showClone && (
        <CloneDialog
          title="Copiar ubicación"
          defaultName={cloneName}
          hint="Se copiará la configuración (tipo, ubicación padre, dirección, edificio/planta, descripción y notas). No se copiará el código ni el plano de planta; los racks siguen siendo de la ubicación original."
          busy={cloning}
          error={cloneError}
          onClose={() => setShowClone(false)}
          onConfirm={handleCloneConfirm}
        />
      )}

      {/* ── Confirmar borrado (diálogo común) ── */}
      {showDelete && (
        <Dialog
          title="Eliminar ubicación"
          busy={saving}
          onClose={() => setShowDelete(false)}
          footer={(
            <>
              <button className="list-btn-secondary" type="button" onClick={() => setShowDelete(false)} disabled={saving}>
                Cancelar
              </button>
              {formData.is_active && (
                <button className="list-btn-secondary" type="button" onClick={handleDeactivate} disabled={saving}>
                  Desactivar
                </button>
              )}
              <button className="list-btn-danger" type="button" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar definitivamente la ubicación <strong>{formData.name}</strong>?{' '}
            No se puede deshacer.
          </p>
          {loadingDelete && <p className="list-hint">Cargando…</p>}
          {!loadingDelete && <LocationDeleteInfo info={deleteInfo} />}
        </Dialog>
      )}
    </div>
  );
}

export default LocationForm;
